import {IndividualContactApiDto} from "../../api/model/rest";
import {none, Option, option} from "ts-option";
import IndividualContactData from "./individual/IndividualContactData";
import IndividualContactMainData from "./individual/IndividualContactMainData";
import IndividualCommunication from "./individual/IndividualCommunication";
import * as moment from "moment";
import Phone from "../phone/Phone";
import Email from "../email/Email";
import DateUtils from "../../utils/DateUtils";
import {isEmail} from "../../utils/ValidationUtils";

/**
 * Individual Contact.
 */
export default class IndividualContact {

	/**
	 * @param {string} id Id of the contact.
	 * @param {IndividualContactData} data of the individual contact.
	 */
	constructor(public readonly id: string,
		public readonly data: IndividualContactData) {}

	/**
	 * Check if contact has mobile phone
	 * @returns {boolean}
	 */
	isOneOfPhoneNumbersMobile(): boolean {
		return this.data.communication.phoneNumbers.map(p => p.some(n => n.isMobile())).getOrElse(false);
	}

	/**
	 * Display mobile numbers of contact.
	 * @returns {string}
	 */
	displayMobileNumbers() {
		return this.data.communication.phoneNumbers.map(
			p => p.filter(pa => pa.isMobile()).map(p => p.internationalPhoneNumber)).getOrElse([]).join(", ")
	}

	/**
	 * Display e-mail addresses of contact.
	 * @returns {string}
	 */
	displayEmailAddresses() {
		return this.data.communication.emails.map(e => e.map(m => m.value)).getOrElse([]).join(", ")
	}

	getPrimaryEmail(): Option<string> {
		return this.data.communication.emails.flatMap(emails => option(emails[0])).map(email => email.value)
	}

	/**
	 * Checks if contact has an email address.
	 */
	hasEmailAddress(): boolean {
		return this.data.communication.emails.getOrElse([]).length !== 0;
	}

	static fromJson(json: IndividualContactApiDto): IndividualContact {
		const birthDate = option(json.dateOfBirth).map(d => moment(d, DateUtils.ISO_DATE_FORMAT, true));
		const mainData = new IndividualContactMainData(option(json.title), json.firstName, json.lastName, birthDate);
		const emails = json.emails && json.emails.length ?
			option(json.emails.map(email => new Email(email, isEmail(email)))) : none;
		const communication = new IndividualCommunication(emails,
			json.phoneNumbers && json.phoneNumbers.length ? option(json.phoneNumbers.map(Phone.fromJson)) : none);
		const communicationConsent = json.communicationConsent ? option(json.communicationConsent) : none;
		const servicePlanRemindersConsent = json.servicePlanRemindersConsent ? option(json.servicePlanRemindersConsent) : none;
		const creationDateTime = moment(json.creationDateTime);
		const data = new IndividualContactData(mainData, communication, option(json.primarySiteId), communicationConsent,
			servicePlanRemindersConsent, creationDateTime, json.brandId, option(json.partnerBrandId), json.archived);
		return new IndividualContact(json.id, data);
	}

	static sortByName(a: IndividualContact, b: IndividualContact) {
		const result = a.data.mainData.lastName.localeCompare(b.data.mainData.lastName);
		if (result === 0) {
			return a.data.mainData.firstName.localeCompare(b.data.mainData.firstName);
		}
		return result;
	}

	copy({id = this.id, data = this.data}) {
		return new IndividualContact(id, data);
	}
}